<script>
import userApi from '@/api/user';
import utils from '@/utils/common';
import DefaultLayout from '@/layouts/Default.vue';
import { userStatuses, getUserStatus } from '../utils/userStatus';
import daysLeft from '../utils/daysLeft';

import CommonSection from '../components/CommonSection.vue';
import FormBlock from '../components/FormBlock.vue';
import BaseTable from '../components/base/BaseTable.vue';
import BaseTableHeaderItem from '../components/base/BaseTableHeaderItem.vue';
import UserTile from '../components/UserTile.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseInput from '../components/BaseInput.vue';
import UserStatus from '../components/user/UserStatus.vue';
import AdminUserListTileCell from '../components/admin/list/AdminUserListTileCell.vue';
import AdminUserListStatusCell from '../components/admin/list/AdminUserListStatusCell.vue';
import BaseSelectMulti from '../components/BaseSelectMulti.vue';
import BaseCheckBoxList from '../components/base/BaseCheckBoxList.vue';
import HeadersSelector from '../components/admin/HeadersSelector.vue';
import BasePopup from '../components/BasePopup.vue';
import BaseTableCell from '../components/base/BaseTableCell.vue';
import AdminUserListTariffCell from '../components/admin/list/AdminUserListTariffCell.vue';

const priceFormatter = new Intl.NumberFormat('ru', { style: 'currency', currency: 'RUB', minimumFractionDigits: 0 });
const formatDate = (date) => utils.convertDateToString(date, 'short');
const formatStorageSpace = (value) => Math.round((value) / 1024 / 1024 / 1024 * 1000) / 1000; // eslint-disable-line
const formatStorage = (item) => `${formatStorageSpace(item.storageCount)} / ${formatStorageSpace(item.tariff.maxStorage)}`;
const formatPhoneNumber = (item) => ((item.phone && item.phone !== '') ? item.phone : '\u2013');

const daysLeftSorter = (a, b) => (a.daysLeft ?? Number.MIN_SAFE_INTEGER) - (b.daysLeft ?? Number.MIN_SAFE_INTEGER);

const headers = [
  { title: 'ID',                width: 3,   component: { is: BaseTableCell,                     props: (item) => ({ value: item.id }) },                                                                                   sortFn: (a, b) => b.id - a.id, defaultSort: true, required: true                 }, // eslint-disable-line
  { title: 'Пользователь',      width: 25,  component: { is: AdminUserListTileCell,             props: (item) => ({ row: item }) },                                                                                        sortFn: (a, b) => a.nameSortIndex.localeCompare(b.nameSortIndex), required: true }, // eslint-disable-line
  { title: 'Статус',            width: 10,  component: { is: AdminUserListStatusCell,           props: (item) => ({ row: item }) },                                                                                        sortFn: (a, b) => a.status.value.localeCompare(b.status.value)                   }, // eslint-disable-line
  { title: 'Тариф',             width: 10,  component: { is: AdminUserListTariffCell,           props: (item) => ({  row: item }) },                                                                                       sortFn: (a, b) => a.tariff.name.localeCompare(b.tariff.name)                     }, // eslint-disable-line
  { title: 'Баланс',            width: 10,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.accountBalanceFormatted, isBold: true }) },                                                sortFn: (a, b) => a.accountBalance - b.accountBalance                            }, // eslint-disable-line
  { title: 'Регистрация',       width: 10,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.createdAtFormatted }) },                                                                   sortFn: (a, b) => a.time - b.time                                                }, // eslint-disable-line
  { title: 'Последний платёж',  width: 10,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.lastPaymentFormatted, optional: item.lastPaymentDateFormatted, isBold: true }) },          sortFn: (a, b) => a.lastPaymentDateSort - b.lastPaymentDateSort                  }, // eslint-disable-line
  { title: 'Всего оплачено',    width: 9,   component: { is: BaseTableCell,                     props: (item) => ({ value: item.paymentsSumFormatted, isBold: true }) },                                                   sortFn: (a, b) => a.paymentsSum - b.paymentsSum                                  }, // eslint-disable-line
  { title: 'Сумма в день',      width: 10,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.pricePerDayFormatted, isBold: true }) },                                                   sortFn: (a, b) => a.tariff.pricePerDay - b.tariff.pricePerDay                    }, // eslint-disable-line
  { title: 'Всего платежей',    width: 9,   component: { is: BaseTableCell,                     props: (item) => ({ value: item.totalPayments }) },                                                                        sortFn: (a, b) => a.totalPayments - b.totalPayments                              }, // eslint-disable-line
  { title: 'Оплачено дней',     width: 9,   component: { is: BaseTableCell,                     props: (item) => ({ value: item.daysLeft ?? '\u2013' }) },                                                                 sortFn: daysLeftSorter                                                           }, // eslint-disable-line
  { title: 'Проекты',           width: 10,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.projectsFormatted }) },                                                                    sortFn: (a, b) => a.projectCount - b.projectCount                                }, // eslint-disable-line
  { title: 'Хранилище, ГБ',     width: 16,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.storageFormatted }) },                                                                     sortFn: (a, b) => a.storageCount - b.storageCount                                }, // eslint-disable-line
  { title: 'Номер телефона',    width: 20,  component: { is: BaseTableCell,                     props: (item) => ({ value: item.phoneFormatted }) }                                                                                                                                                         }, // eslint-disable-line
];

const presets = [
  { label: 'Общая',           options: [ 'Регистрация', 'Тариф', 'Баланс', 'Статус', 'Последний платёж']                                             }, // eslint-disable-line
  { label: 'Платежи',         options: ['Тариф', 'Баланс', 'Сумма в день', 'Всего платежей', 'Всего оплачено', 'Последний платёж', 'Оплачено дней'] }, // eslint-disable-line
  { label: 'Сводная таблица', options: ['Проекты', 'Хранилище, ГБ']                                                                                 }, // eslint-disable-line
  { label: 'Партнерство',     options: ['Всего платежей', 'Всего оплачено']                                                                         }, // eslint-disable-line
  // { label: 'География',       options: ['Номер телефона']                                                                                           }, // eslint-disable-line
];

export default {
  name: 'AdminUserList2',
  components: {
    BasePopup,
    BaseCheckBoxList,
    FormBlock,
    CommonSection,
    DefaultLayout,
    BaseTable,
    BaseTableHeaderItem,
    UserTile,
    BaseSelect,
    BaseInput,
    UserStatus,
    BaseSelectMulti,
    HeadersSelector,
  },
  statuses: {
    active: 'Активен',
    blocked: 'Заблокирован',
    notConfirmed: 'Не активен',
  },
  data() {
    return {
      users: [],
      userFilters: {
        tariff: null,
        status: null,
        name: '',
      },
      headers: [],
      selectedHeaders: [],
      isPopupOpened: false,
    };
  },
  created() {
    userApi.fetchList({}, true)
      .then((r) => {
        this.users = r.items;
      });

    this.$store.dispatch('tariff/fetchItems');
    this.selectedHeaders = presets[0].options;
  },
  computed: {
    presets() { return presets; },

    filteredHeaders() {
      const requiredHeaders = headers.filter((header) => header.required);
      const selectedHeaders = headers.filter((header) => this.selectedHeaders.includes(header.title));
      return [...requiredHeaders, ...selectedHeaders];
    },

    headersOptions() { return headers.filter((header) => !header.required).map((header) => header.title); },

    headersWidthOverflow() {
      const width = headers
        .filter((header) => header.required || this.selectedHeaders.includes(header.title))
        .reduce((acc, value) => acc + (value.width + 0.1), 0);
      return width > 100;
    },

    statusOptions() { return userStatuses; },

    tariffOptions() {
      const tariffsOptions = this.$store.state.tariff.items.map((t) => ({ label: t.name, value: t.id }));

      tariffsOptions.push({ label: 'Все платные', value: 'paid' });
      tariffsOptions.push({ label: 'Все бесплатные', value: 'free' });
      return tariffsOptions;
    },

    modifiedUsers() {
      return this.users.map((u) => {
        const payments = (u.payments ?? []).filter((p) => p.status === 'confirmed' && p.createdAt);

        const lastPayment = payments.length ? [...payments].pop() : null;
        const paymentsSum = payments.reduce((acc, value) => acc + value.amount, 0);
        const paymentsCount = payments.length;

        return {
          ...u,
          createdAt: utils.convertDateToString(new Date(u.createdAt)),
          time: this.getTimeFromDate(u.createdAt),
          selectedTariff: u.tariff['@id'],
          lastPaymentDateSort: new Date(lastPayment?.createdAt ?? null).getTime(),
          lastPaymentDateFormatted: lastPayment ? formatDate(new Date(lastPayment.createdAt)) : null,
          totalPayments: paymentsCount,
          paymentsSum,
          nameSortIndex: ((u.name) ? (u.name + u.surname) : u.email).toLowerCase().replace(/ /g, ''),
          status: getUserStatus(u),
          daysLeft: daysLeft(u),
          tariffValue: { label: u.tariff.name, id: u.tariff.id },
          paymentsConfirmed: payments,
          lastPaymentFormatted: lastPayment ? priceFormatter.format(Math.round(lastPayment.amount / 100)) : '\u2013',
          accountBalanceFormatted: priceFormatter.format(Math.round(u.accountBalance / 100)),
          createdAtFormatted: u.createdAt ? formatDate(new Date(u.createdAt)) : '\u2013',
          paymentsSumFormatted: priceFormatter.format(Math.round(paymentsSum / 100)),
          pricePerDayFormatted: `${priceFormatter.format(Math.round(u.tariff.pricePerDay / 100))}/ день`,
          projectsFormatted: `${u.projectCount} / ${u.tariff.maxProjects}`,
          storageFormatted: formatStorage(u),
          phoneFormatted: formatPhoneNumber(u),
        };
      });
    },

    filteredUsers() {
      return this.modifiedUsers.filter((u) => (!this.userFilters.status || u.status.value === this.userFilters.status)
        && (!this.userFilters.tariff || this.userFilters.tariff === (u.tariff.id) || (this.userFilters.tariff === 'free' && u.tariff.pricePerDay === 0) || (this.userFilters.tariff === 'paid' && u.tariff.pricePerDay > 0))
        && (!this.userFilters.name || (u.id + u.name + u.surname + u.email).toLowerCase().includes(this.userFilters.name.toLowerCase().replace(/ /g, ''))));
    },
  },
  methods: {
    findLastPayment(payments) {
      const confirmedPayments = (payments ?? [])
        .filter(p => p.status === 'confirmed' && p.createdAt)
        .map(p => ({ amount: p.amount, date: formatDate(new Date(p.createdAt)) }));

      return confirmedPayments.pop();
    },
    findTotalPaymentsCount(payments) {
      const confirmedPayments = (payments ?? []).filter((payment) => payment.status === 'confirmed');
      return confirmedPayments.length;
    },
    getTimeFromDate(date) { return new Date(date).getTime(); },
  },
};

</script>

<template lang="pug">
DefaultLayout
  BasePopup(v-if="isPopupOpened" @close="isPopupOpened = false")
    .popup-select
      .popup-info
        .popup-header Настройки
        .popup-description Выберите параметры, которые вы хотите увидеть в таблице.
        .popup-description.popup-error(v-if="headersWidthOverflow") Превышена максимальная ширина таблицы
      BaseCheckBoxList.list(v-model="selectedHeaders" :options="headersOptions")
      Btn.submit(round xl :blue="true" @click="isPopupOpened = false") Закрыть
  CommonSection.b-user-list(v-if="users" title="Пользователи")
    template(#button)
      .total Всего {{ modifiedUsers.length }}
    .table-header
      .filters
          BaseSelect(v-model="userFilters.tariff" label="Тариф" :options="tariffOptions")
          BaseSelect(v-model="userFilters.status" label="Статус" :options="statusOptions")
          BaseInput(v-model="userFilters.name" label="Имя/e-mail" )
      .selectors
        HeadersSelector(v-model="selectedHeaders" :presets="presets")
      .tools
        Icon.show-checkbox(name="setting" pointer @click.native="isPopupOpened = true")._show-checkbox-popup
    FormBlock._main
      BaseTable._table(:headers="filteredHeaders" :items="filteredUsers" :pageSize="96")
</template>

<style scoped lang="scss">
.popup-select {
  display: flex;
  flex-direction: column;

  .popup-info {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 34px;
  }

  .list {
    width: 100%;
    padding-bottom: 40px;
  }

  .popup-header {
    font-family: Geometria, sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: 24px;
  }

  .popup-description {
    color: #92A0B0;
    font-family: Geometria, sans-serif;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 24px; /* 171.429% */
  }

  .popup-error {
    color: red;
  }
}

.tools {
  background-color: rgba(235, 239, 242, 1);
  border-radius: 4px;
  padding: 10px 7px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .show-checkbox {
    fill: transparent;
    stroke: rgba(120, 140, 161, 1);
  }
}

.user-list {
  display: flex;
  flex-direction: column;
  position: relative;

  .selectors {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    gap: 5px;
  }

  .table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 35px;
  }

  .total {
    background: #FDDCC4;
    border-radius: 6px;
    display: inline-flex;
    max-width: 110px;
    align-items: center;
    text-align: center;
    letter-spacing: 0.01em;
    font-weight: 400;
    font-size: 14px;
    height: 39px;
    box-sizing: border-box;
    padding: 0 15px;
  }

  &__main {
    position: relative;
  }

  .filters {
    display: flex;
    flex-direction: row;
    gap: 10px;
  }
}
</style>
